import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@mui/styles';
import {
  dequeueTour,
  queueTour,
  advanceTourStep,
  goBackTourStep,
  completeAllSteps
} from 'state/modules/tour';
import { getCurrentStep } from 'state/modules/tour/selectors';
import TourBtnActionGroup from './common/TourBtnActionGroup';
import { getElement, getRectMap, LIGHT_ME_UP } from './util';
import CalloutMessage from './common/CalloutMessage';
import { RectMap, StepMap, DOMId } from './types';

export type ZindexState = [DOMId, string];

type Props = {
  tourName: string;
  stepMap?: StepMap;
  getTourBtnActionGroupStyle?: (currentStep: any) => { [key: string]: any };
  children: any;
};

const TourContainer: React.FC<Props> = ({
  tourName,
  stepMap = {},
  getTourBtnActionGroupStyle,
  ...props
}) => {
  const [prevElZindex, setZindexOverrides] = useState<ZindexState>([] as any);
  const [rectMap, setRectMap] = useState<RectMap>();
  const tourState = useSelector(({ tour }: any) => tour);
  const currentStep = getCurrentStep(tourState);
  const totalSteps = tourState?.allTours?.[tourName]?.steps?.length;
  const groupName = currentStep?.groupName;

  const classes = getStyles()();

  const dispatch = useDispatch() as any as any;

  // kick off tour
  useEffect(() => {
    if (!tourState.queuedTours.includes(tourName)) {
      dispatch(queueTour(tourName, true));
    }
  }, [dispatch, tourState, tourName]);

  // build map of hijacked element positions
  useEffect(() => {
    if (!rectMap && stepMap) setRectMap(getRectMap(stepMap));
  }, [rectMap, stepMap]);

  // LIGHT UP DOM ELEMENTS
  useEffect(() => {
    if (!groupName) return;

    if (prevElZindex.length && prevElZindex[0] !== groupName) {
      const prevEl = getElement(prevElZindex[0], stepMap);
      if (prevEl) prevEl.style.zIndex = String(prevElZindex[1]);
      setZindexOverrides([] as any);
    }

    const nextEl = getElement(groupName, stepMap);

    if (nextEl && String(nextEl.style.zIndex) !== String(LIGHT_ME_UP)) {
      setZindexOverrides([groupName, nextEl.style.zIndex ?? 'inherit']);
      nextEl.style.zIndex = String(LIGHT_ME_UP);
    }
  }, [groupName, prevElZindex, setZindexOverrides, stepMap]);

  const completeTour = () => {
    dispatch(completeAllSteps());
    dispatch(dequeueTour());
  };

  const finishTour = () => {
    dispatch(advanceTourStep());
    dispatch(dequeueTour());
  };

  const handleGoBackStep = () => dispatch(goBackTourStep());

  const handleGoToNextStep = () => dispatch(advanceTourStep());

  const handlegetTourBtnActionGroupStyle = () => getTourBtnActionGroupStyle?.(currentStep);

  return (
    <div className={classes.root}>
      <div className={classes.calloutMessageContainer}>
        <CalloutMessage classes={classes} currentStep={currentStep} rectMap={rectMap} />
      </div>
      {props.children}
      <TourBtnActionGroup
        getTourBtnActionGroupStyle={handlegetTourBtnActionGroupStyle}
        isFirstStep={tourState.currentStep === 0}
        isLastStep={tourState.currentStep === totalSteps.length - 1}
        currentStep={currentStep}
        onFinish={finishTour}
        onSkip={completeTour}
        onGoBackStep={handleGoBackStep}
        onGoToNextStep={handleGoToNextStep}
      />
    </div>
  );
};

const getStyles = () =>
  makeStyles({
    root: {
      position: 'relative'
    },
    calloutMessageContainer: {
      position: 'absolute',
      top: '-67px',
      bottom: '-68px',
      right: 0,
      left: 0,
      zIndex: LIGHT_ME_UP - 1,
      background: '#202020e5',
      display: 'flex',
      flexDirection: 'column'
    },
    hintImage: {
      maxWidth: '100%',
      maxHeight: '100%'
    },
    hint: {
      position: 'absolute',
      left: 0,
      right: 0,
      display: 'flex',
      padding: 0
    }
  });

export default TourContainer;
